import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { OfferingType } from 'app/library/course-offering/enums/offering-type.enum';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICourseStructureCategoryTreeQuery } from '../interfaces/course-structure-category-tree.query.interface';
import { ICourseStructureCategoryQuery } from '../interfaces/course-structure-category.query.interface';
import { CourseStructureCategoryTree } from '../models/course-structure-category-tree.model';
import { CourseStructureCategoryCommand } from '../models/course-structure-category.command.model';
import { CourseStructureCategory } from '../models/course-structure-category.model';
import { CourseStructureSubcategory } from '../models/course-structure-subcategory.model';
import { ICourseStructureSubcategoryQuery } from '../interfaces/course-structure-subcategory.query.interface';
import { ICourseStructureSubCategoryActionPayload } from '../interfaces/course-structure-subcategory.action-payload.interface';

@Injectable()
export class CourseStructureCategoryApiService
    extends EntityService<ICourseStructureCategoryQuery, CourseStructureCategoryCommand>
    implements IEntityApiService<CourseStructureCategory>
{
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'course-structure/categories');
    }

    getAll(): Observable<CourseStructureCategory[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new CourseStructureCategory(query))));
    }

    create(entity: CourseStructureCategory): Observable<CourseStructureCategory> {
        const data = new CourseStructureCategoryCommand(entity);

        return this._create(data).pipe(map((query) => new CourseStructureCategory(query)));
    }

    read(id: string): Observable<CourseStructureCategory> {
        return this._read(id).pipe(map((query) => new CourseStructureCategory(query)));
    }

    update(entity: CourseStructureCategory): Observable<CourseStructureCategory> {
        const data = new CourseStructureCategoryCommand(entity);
        return this.http.patch<ICourseStructureCategoryQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new CourseStructureCategory(query)));
    }

    updateOrder(entity: CourseStructureCategory): Observable<CourseStructureCategory> {
        const data = new CourseStructureCategoryCommand(entity);
        return this.http.patch<ICourseStructureCategoryQuery>(`${this.apiUrl}/${this.entities}/${entity.id}/order`, data).pipe(map((query) => new CourseStructureCategory(query)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    search(
        search: string,
        fetchCourseStructureCategoriesDataAsTree: boolean,
        courseOfferingType: OfferingType,
        tags: string[] = [],
        types: string[],
        extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [],
        columns: string[] = []
    ): Observable<CourseStructureCategoryTree[]> {
        let params = new HttpParams();

        // By default it returns array without children, if we send query param tree set to true, we get tree structure of categories
        if (fetchCourseStructureCategoriesDataAsTree) {
            params = params.set('tree', fetchCourseStructureCategoriesDataAsTree.toString());
        }

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (courseOfferingType) {
            params = params.set('filter[coType]', courseOfferingType);
        }

        if (tags.length) {
            params = params.set('filter[tag]', tags.toString());
        }

        if (extendedAttributesFilters?.length) {
            let attributeValues = {};

            extendedAttributesFilters.forEach((attr) => {
                if (attr.value) {
                    if (!attributeValues[attr.attributeName]) {
                        attributeValues[attr.attributeName] = [];
                    }

                    attributeValues[attr.attributeName].push(attr.value);
                } else {
                    params = params.set(`filter[${attr.attributeName}][gte]`, attr.rangeValue1);
                    params = params.set(`filter[${attr.attributeName}][lte]`, attr.rangeValue2);
                }
            });

            Object.keys(attributeValues).forEach((key) => {
                params = params.append(`filter[${key}]`, attributeValues[key].join(','));
            });
        }

        if (columns?.length) {
            params = params.set('filter[columns]', columns.map((column) => column).toString());
        }

        if (types?.length) {
            params = params.append(`filter[type]`, types.map((type) => type).toString());
        }

        return this.http
            .get<ICourseStructureCategoryTreeQuery[]>(`${this.apiUrl}/${this.entities}/search`, { params })
            .pipe(map((queries) => queries.map((query) => new CourseStructureCategoryTree(query))));
    }

    fetchCourseStructureCategoriesTree(courseOfferingType: number): Observable<CourseStructureCategoryTree[]> {
        let params = new HttpParams();

        if (courseOfferingType) {
            params = params.set('filter[coType]', courseOfferingType);
        }

        return this.http
            .get<ICourseStructureCategoryTreeQuery[]>(`${this.apiUrl}/${this.entities}/tree`, { params })
            .pipe(map((queries) => queries.map((query) => new CourseStructureCategoryTree(query))));
    }

    fetchCategorySubcategories(categoryId: string): Observable<CourseStructureSubcategory[]> {
        return this.http
            .get<ICourseStructureSubcategoryQuery[]>(`${this.apiUrl}/${this.entities}/${categoryId}/sub-categories`)
            .pipe(map((queries) => queries.map((query) => new CourseStructureSubcategory(query))));
    }

    moveCourseStructureCategory(categoryId: string, parentId: string): Observable<CourseStructureCategory> {
        return this.http
            .post<ICourseStructureCategoryQuery>(`${this.apiUrl}/${this.entities}/${categoryId}/move`, { parent_id: parseInt(parentId) })
            .pipe(map((query) => new CourseStructureCategory(query)));
    }

    createSubcategory(entity: ICourseStructureSubCategoryActionPayload): Observable<CourseStructureSubcategory> {
        return this.http.post<ICourseStructureSubcategoryQuery>(`${this.apiUrl}/${this.entities}`, entity).pipe(map((query) => new CourseStructureSubcategory(query)));
    }
}
